try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsxs, jsx } from 'react/jsx-runtime';
import { Button } from '@design-system/button';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Icon } from '@design-system/icon';
import { SearchInput } from '@design-system/search-input';
import { Typography } from '@design-system/typography';
import { isBrowser } from '@phx-husky/browser-helpers';
import { getLocalStorageData, setLocalStorageData } from '@phx-husky/local-storage';
import { useScreenRecognition } from '@phx-husky/use-screen-recognition';

const readUserSearches = (localStorageLastSearchesKey) => isBrowser() ? getLocalStorageData(localStorageLastSearchesKey) : null;
const restoreUserSearches = (localStorageData) => localStorageData === null ? [] : localStorageData;
const writeUserSearches = (lastUserSearches, localStorageLastSearchesKey) => setLocalStorageData(localStorageLastSearchesKey, lastUserSearches);
const isAlreadyAdded = (lastUserSearches, searchRequest) => lastUserSearches.indexOf(searchRequest);
const formatUserSearchWithExistingRequest = (searchRequest, lastUserSearches, foundIndex) => [
    searchRequest,
    ...lastUserSearches.slice(0, foundIndex),
    ...lastUserSearches.slice(foundIndex + 1),
];
const getUserSearches = (localStorageLastSearchesKey) => restoreUserSearches(readUserSearches(localStorageLastSearchesKey));
const saveUserSearch = (searchRequest, lastSearchesQuantity, localStorageLastSearchesKey) => {
    if (!searchRequest)
        return;
    const lastUserSearches = getUserSearches(localStorageLastSearchesKey);
    const foundIndex = isAlreadyAdded(lastUserSearches, searchRequest);
    const newSearches = foundIndex !== -1
        ? formatUserSearchWithExistingRequest(searchRequest, lastUserSearches, foundIndex)
        : [searchRequest, ...lastUserSearches.slice(0, lastSearchesQuantity - 1)];
    writeUserSearches(newSearches, localStorageLastSearchesKey);
};
const handleNavigation = (searchText, customNavigate) => {
    if (customNavigate) {
        customNavigate(`?q=${searchText}`);
    }
    else {
        window.location.href = new URL(`?q=${searchText}`, document.baseURI).href;
    }
};

const SearchBlockContainer = forwardRef(({ ariaLabelClose, onClick, searchInputPlaceholder, swipeLabel, isMobile, userSearches, handleSetUserSearches, customNavigate, localStorageLastSearchesKey, lastSearchesQuantity, lastSearchesTitle, searchButtonText, searchPageUrl, autocomplete, handleBurgerMenu, }, ref) => {
    var _a, _b;
    const inputRef = useRef(null);
    const handleSearch = () => {
        const searchText = inputRef.current.value;
        saveUserSearch(searchText, lastSearchesQuantity, localStorageLastSearchesKey);
        handleSetUserSearches();
        handleNavigation(searchText, customNavigate);
        onClick();
        handleBurgerMenu === null || handleBurgerMenu === void 0 ? void 0 : handleBurgerMenu();
    };
    const handleKeyDown = (event) => {
        if (!inputRef.current || event.key !== 'Enter')
            return;
        handleSearch();
    };
    return (jsxs("div", Object.assign({ ref: ref, "data-testid": "search-block-container" }, { children: [jsx(Button, Object.assign({ className: "cds-search-block__close", variant: "subtle-icon", size: "large", iconPrefix: { props: { icon: 'close' } }, onClick: onClick }, { children: ariaLabelClose })), jsxs("div", Object.assign({ className: "cds-search-block__search-bar-container" }, { children: [jsxs("div", Object.assign({ className: "cds-search-block__input-container" }, { children: [jsx(Icon, { icon: "search" }), jsx(SearchInput, { onKeyDown: handleKeyDown, placeholder: searchInputPlaceholder, inputRef: inputRef, searchPageUrl: searchPageUrl, autocomplete: autocomplete })] })), jsx("div", Object.assign({ className: "cds-search-block__search-btn-container" }, { children: jsx(Button, Object.assign({ size: "large", variant: "secondary", className: "cds-search-block__search-btn", onClick: handleSearch }, { children: searchButtonText })) })), (userSearches === null || userSearches === void 0 ? void 0 : userSearches.length) && ((_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.length) < 3 ? (jsxs("div", Object.assign({ className: "cds-search-block__last-searches-container" }, { children: [jsx(Typography, { className: "cds-search-block__last-searches-title", variant: "headline", size: "s", content: lastSearchesTitle }), userSearches.map((search) => (jsx(Button, Object.assign({ id: "search-button", size: "small", variant: "secondary", iconPrefix: { props: { icon: 'search' } }, className: "cds-search-block__last-search", onClick: () => {
                                    handleNavigation(search, customNavigate);
                                    onClick();
                                    handleBurgerMenu === null || handleBurgerMenu === void 0 ? void 0 : handleBurgerMenu();
                                } }, { children: search }), search)))] }))) : null] })), isMobile ? (jsxs("div", Object.assign({ className: "cds-search-block__swipe" }, { children: [jsx(Icon, { icon: "arrowUp" }), jsx(Button, Object.assign({ size: "large", variant: "subtle", onClick: onClick }, { children: swipeLabel }))] }))) : null] })));
});

const useBrowserScreen = () => {
    const { isMobile } = useScreenRecognition();
    const isMobileView = isBrowser() && isMobile;
    return { isMobileView };
};

const SearchBlock = forwardRef(({ className, ariaLabelOpen, ariaLabelClose, swipeLabel, searchInputPlaceholder, searchLabel, customNavigate, localStorageLastSearchesKey, lastSearchesQuantity, lastSearchesTitle, searchButtonText, autocomplete, searchPageUrl, handleBurgerMenu, }, ref) => {
    const [userSearches, setUserSearches] = useState(getUserSearches(localStorageLastSearchesKey));
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [isSwipe, setIsSwipe] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const blockRef = useRef(null);
    const { isMobileView: isMobile } = useBrowserScreen();
    const defaultBlockClass = 'cds-search-block__container';
    const activeBlockClass = 'cds-search-block__container cds-search-block__container--active';
    const mobileBlockClass = 'cds-search-block__container cds-search-block__container--mobile';
    const enterBlockClasses = isSwipe ? mobileBlockClass : activeBlockClass;
    const handleClickOutside = useCallback((event) => {
        var _a;
        if (!((_a = blockRef === null || blockRef === void 0 ? void 0 : blockRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            setIsSearchVisible(false);
        }
    }, [blockRef]);
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [blockRef]);
    const onClick = useCallback(() => {
        setIsSearchVisible(!isSearchVisible);
        if (isMobile) {
            setIsSwipe(!isSwipe);
        }
    }, [isSearchVisible, isMobile, setIsSwipe]);
    const handleTouch = useCallback((event) => {
        if (event.type === 'touchstart') {
            setTouchStart(event.targetTouches[0].clientY);
            setTouchEnd(event.targetTouches[0].clientY);
        }
        if (event.type === 'touchmove') {
            setTouchEnd(event.targetTouches[0].clientY);
        }
    }, []);
    const handleTouchEnd = useCallback(() => {
        if (isMobile && touchStart > 150 && touchStart > touchEnd) {
            onClick();
        }
    }, [isMobile, touchStart, touchEnd, onClick]);
    const handleSetUserSearches = useCallback(() => {
        setUserSearches(getUserSearches(localStorageLastSearchesKey));
    }, [localStorageLastSearchesKey]);
    return (jsxs("div", Object.assign({ ref: ref, onTouchStart: handleTouch, onTouchMove: handleTouch, onTouchEnd: handleTouchEnd, className: classNames('cds-search-block', className), "data-testid": "cds-search-block" }, { children: [jsx(Button, Object.assign({ variant: searchLabel ? 'nav' : 'subtle-icon', iconSuffix: { props: { icon: 'search' } }, "aria-label": ariaLabelOpen, onClick: onClick, className: "cds-btn__search-icon" }, { children: searchLabel || '' })), jsx(CSSTransition, Object.assign({ nodeRef: blockRef, in: isSearchVisible, timeout: 300, classNames: {
                    appear: defaultBlockClass,
                    appearActive: defaultBlockClass,
                    appearDone: defaultBlockClass,
                    enter: enterBlockClasses,
                    enterDone: enterBlockClasses,
                    exit: defaultBlockClass,
                    exitActive: defaultBlockClass,
                    exitDone: defaultBlockClass,
                }, unmountOnExit: true }, { children: jsx(FocusTrap, { children: jsx(SearchBlockContainer, { ariaLabelClose: ariaLabelClose, ariaLabelOpen: ariaLabelOpen, isMobile: isMobile, onClick: onClick, ref: blockRef, searchInputPlaceholder: searchInputPlaceholder, swipeLabel: swipeLabel, userSearches: userSearches, handleSetUserSearches: handleSetUserSearches, customNavigate: customNavigate, localStorageLastSearchesKey: localStorageLastSearchesKey, lastSearchesQuantity: lastSearchesQuantity, lastSearchesTitle: lastSearchesTitle, searchButtonText: searchButtonText, autocomplete: autocomplete, searchPageUrl: searchPageUrl, handleBurgerMenu: handleBurgerMenu }) }) })), jsx("div", { className: classNames('cds-search-block__mask', {
                    'cds-search-block__mask--active': isSearchVisible,
                }) })] })));
});

export { SearchBlock };
